import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import Image from '@components/Image';
import { BaseModal } from '@repo/components/modals';
import Action from '@components/Button/Action';
import Header from '@components/Modals/Header';
import Item from '@components/CheckMarkItem';
import styles from './Modal.module.scss';
import { TrackingFn } from '@components/Integrations/utils';
import { useSelector } from 'react-redux';
import {
  selectIntegrationByPlatform,
  selectIntegrationStatusOnDevice,
} from '@repo/redux/reducers/user/integrations';
import { syncDeviceIntegration } from '@repo/redux/actions/deviceIntegrations';
import { supportEmailProps } from '@repo/utils/helpers/preferred-email-client';

const whatsapp = '/img/settings/whatsapp-dark.png';
const whatsappLight = '/img/settings/whatsapp-light.png';

type ModalRenderLocation = 'settings' | 'onboarding';

interface DetailsProps {
  onSync: () => void;
  renderLocation: ModalRenderLocation;
}

function Details({ onSync, renderLocation }: DetailsProps) {
  const forceDarkMode = renderLocation === 'onboarding';

  return (
    <div className={styles.content}>
      <p className="text-secondary">Connect your Whatsapp in a few simple steps</p>

      <Item title="Secure">
        People you message will be imported locally, Your information is encrypted end-to-end.
      </Item>
      <Item title="Private">Clay never reads or imports message data.</Item>
      <Item title="Limited Access">
        Clay will never text anyone or take actions on your behalf.
      </Item>

      <div className={styles.actions}>
        <Action onClick={onSync} block forceDarkMode={forceDarkMode}>
          Sync connections
        </Action>
        <div>
          Need help? Visit our{' '}
          <a href="https://go.clay.earth/whatsapp" target="_blank">
            FAQ
          </a>{' '}
          or <a {...supportEmailProps({ subject: 'WhatsApp integration help' })}>message us</a>.
        </div>
      </div>
    </div>
  );
}

function DetailsWeb() {
  return (
    <div className={styles.content}>
      <p>
        To connect your WhatsApp to Clay, please use the Mac, Windows, or iOS apps.{' '}
        <a href="https://clay.earth/start" target="_blank">
          Get started with our other apps.
        </a>
      </p>
    </div>
  );
}

function renderTitle(status, _progress, total) {
  if (status === 'waiting') {
    return 'Waiting for WhatsApp';
  }

  if (status === 'syncing') {
    return <>Syncing connections&hellip;</>;
  }

  return `Successfully imported ${total} connections`;
}

interface WhatsappModalProps {
  // open: boolean;
  onClose: () => void;
  renderLocation: ModalRenderLocation;
  trackConnection: TrackingFn;
  autoSync?: boolean;
}

export default function WhatsappModal({
  onClose,
  renderLocation,
  trackConnection,
  autoSync,
}: WhatsappModalProps) {
  const integration = useSelector(selectIntegrationByPlatform('whatsapp'));
  const integrationStateOnDevice = useSelector(selectIntegrationStatusOnDevice('whatsapp'));
  const [status, setStatus] = useState(integrationStateOnDevice?.syncing ? 'syncing' : null);

  const onSync = useCallback(() => {
    setStatus('waiting');
    trackConnection({ type: 'WhatsApp' });

    if (integrationStateOnDevice?.supported) {
      syncDeviceIntegration('whatsapp');
    }
  }, []);

  useEffect(() => {
    if (integrationStateOnDevice?.syncing && status !== 'syncing') {
      setStatus('syncing');
    } else if (!integrationStateOnDevice?.syncing && status === 'syncing') {
      setStatus('complete');
    }
  }, [integrationStateOnDevice?.syncing, status]);

  useEffect(() => {
    if (renderLocation == 'onboarding' && status !== null) {
      onClose();
    }
  }, [status]);

  useEffect(() => {
    if (autoSync) {
      onSync();
    }
  }, []);

  const detailView = status !== null ? 'status' : window.app.isElectron ? 'details' : 'detailsWeb';
  const forceDarkMode = renderLocation === 'onboarding';

  return (
    <BaseModal
      className={cn(styles.modalWrapper, 'modal-large')}
      forceDarkMode={forceDarkMode}
      onRequestClose={onClose}
      isOpen
      centered
    >
      <Header.Plain onClose={onClose} />
      <div className={styles.content}>
        <Image
          className={styles.image}
          width={207}
          dark={whatsapp}
          light={!forceDarkMode && whatsappLight}
        />

        {detailView === 'details' && <Details onSync={onSync} renderLocation={renderLocation} />}

        {detailView === 'detailsWeb' && <DetailsWeb />}

        {detailView === 'status' && (
          <>
            <h3 className="text-default">
              {renderTitle(
                status,
                integrationStateOnDevice?.progress || 0,
                integration?.status?.total,
              )}
            </h3>

            {status !== 'complete' && (
              <p className={styles.secondary}>
                Please keep the Clay app open until the import is complete. You may close this
                modal.
              </p>
            )}

            {status === 'complete' && (
              <>
                <p className={styles.secondary}>
                  Your connections will begin to appear in Clay shortly. The number of connections
                  ultimately imported may be slightly less than {integration?.status?.total} due to
                  deleted or duplicate profiles.
                </p>
                <p className={styles.secondary}>
                  <a className="link-inherit" href="https://go.clay.earth/whatsapp" target="_blank">
                    Learn more
                  </a>{' '}
                  about our WhatsApp integration.
                </p>
              </>
            )}

            <div className={styles.actions}>
              <Action onClick={onClose} block forceDarkMode={forceDarkMode}>
                Done
              </Action>
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
}
